<app-dialog [visible]="visible" (closed)="onClose()" class="w-[90vw] max-w-[75rem] h-[80vh]">
    <ng-template #headerTemplate>
        <div class="flex items-center justify-between w-full">
            <h3 class="font-bold text-base sm:text-lg">{{ getTitle() }}</h3>
        </div>
    </ng-template>
    <ng-template #bodyTemplate>
        <!-- Radio Buttons: With KB / Without KB -->
        <div class="mb-4">
            <div class="text-sm font-medium text-gray-700 mb-3">How do you want to upload?</div>
            <div class="flex items-center gap-6">
                <div class="flex items-center gap-2">
                    <p-radioButton [value]="'withKB'" [(ngModel)]="uploadMode" name="uploadMode" inputId="withKB"
                        (onClick)="onUploadModeChange('withKB')"></p-radioButton>
                    <label for="withKB" class="cursor-pointer text-sm text-gray-700">With knowledge base</label>
                </div>
                <div class="flex items-center gap-2">
                    <p-radioButton [value]="'withoutKB'" [(ngModel)]="uploadMode" name="uploadMode" inputId="withoutKB"
                        (onClick)="onUploadModeChange('withoutKB')"></p-radioButton>
                    <label for="withoutKB" class="cursor-pointer text-sm text-gray-700">Without knowledge base</label>
                </div>
            </div>
        </div>

        <div class="flex flex-row gap-4 h-full overflow-hidden">
            <!-- Left Panel: Project Selection (if needed) and Knowledge Base Selection -->
            <div class="flex flex-col flex-1 shadow-sm bg-white min-w-0 min-h-0 overflow-hidden pr-4 border-r">
                <!-- Project Dropdown (for workspace documents in project knowledge) -->
                @if(needsProjectSelection) {
                <div class="mb-4 pb-4 border-b">
                    @if(uploadMode === 'withKB') {
                    <div class="mb-2">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Select Project</label>
                        <app-select [(value)]="selectedProjectId" [options]="projectOptions"
                            [config]="getProjectSelectConfig()" (onChange)="onProjectChange($event.value)">
                        </app-select>
                    </div>
                    } @else {
                    <div class="mb-2">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Select Projects</label>
                        <app-multi-select [(value)]="selectedProjectIds" [options]="projectOptions"
                            [config]="getProjectMultiSelectConfig()" (onChange)="onProjectsChange($event.value)">
                        </app-multi-select>
                    </div>
                    }
                </div>
                }

                <!-- Knowledge Base Selection (only for "With KB" mode) -->
                @if(uploadMode === 'withKB') {
                <div class="flex flex-col gap-3 mb-3 flex-shrink-0">
                    <div class="flex items-center w-full gap-2">
                        <app-search-bar placeholder="Search with name" width="100%" [(ngModel)]="searchValue"
                            class="flex-1" (searchChange)="onSearchValueChange($event)">
                        </app-search-bar>
                        <app-button variant="outlined" severity="contrast" [pTooltip]="'Create Knowledge Base'"
                            tooltipPosition="top" icon="pi pi-plus" (clicked)="onCreateKnowledgeBase()"
                            [enableThrottle]="true" />
                    </div>
                    <div class="flex items-center gap-2">
                        <app-checkbox [value]="isAllSelected()" (onChange)="onToggleSelectAll($event)"
                            [config]="{ binary: true, label: 'Select all (' + filteredKnowledgeBases.length + ')' }">
                        </app-checkbox>
                    </div>
                </div>
                <div class="flex-1 overflow-y-auto" (scroll)="onScroll($event)">
                    @if(loading) {
                    <div class="flex flex-col gap-2">
                        @for(item of [1,2,3,4,5,6]; track $index) {
                        <p-skeleton width="100%" height="80px"></p-skeleton>
                        }
                    </div>
                    } @else if(filteredKnowledgeBases.length === 0 && !isLoadingMore) {
                    <div class="flex flex-col items-center justify-center h-full text-center p-4">
                        @if(needsProjectSelection && !selectedProjectId) {
                        <p class="text-gray-500">Please select a project to view knowledge bases.</p>
                        } @else {
                        <p class="text-gray-500">No knowledge bases found.</p>
                        }
                    </div>
                    } @else {
                    <div class="flex flex-col gap-2">
                        @for(kb of filteredKnowledgeBases; track kb._id) {
                        <div class="border rounded-base p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                            (click)="toggleSelection(kb, $event)">
                            <div class="flex items-center gap-3 min-w-0">
                                <app-checkbox [value]="isSelected(kb._id)" (onChange)="onCheckboxChange(kb, $event)"
                                    [config]="{ binary: true }" class="flex-shrink-0"
                                    (click)="$event.stopPropagation()" />
                                <i [innerHTML]="getIcon() | sanitizeHtml" class="flex-shrink-0 w-6 h-6"></i>
                                <div class="min-w-0 flex-1">
                                    <div class="text-sm font-medium text-gray-900 truncate" [pTooltip]="kb.name"
                                        tooltipPosition="top">
                                        {{ kb.name || 'Kb name' }}
                                    </div>
                                    <div class="text-xs text-gray-500 truncate">
                                        {{ kb.description || 'Kb description' }}
                                    </div>
                                    <div class="text-xs text-gray-500 mt-1">
                                        {{ (kb.files && kb.files.length) || 0 }} files
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                        @if(isLoadingMore) {
                        <div class="flex flex-col gap-2">
                            @for(item of [1,2,3]; track $index) {
                            <p-skeleton width="100%" height="80px"></p-skeleton>
                            }
                        </div>
                        }
                    </div>
                    }
                </div>
                } @else {
                <!-- Without KB mode - show message -->
                <div class="flex flex-col items-center justify-center h-full text-center p-4">
                    @if(scope === 'workspace') {
                    <p class="text-gray-500">Documents will be linked to workspace.</p>
                    } @else {
                    @if(hasProject) {
                    <p class="text-gray-500">Documents will be linked to project.</p>
                    } @else {
                    @if(selectedProjectIds.length === 0) {
                    <p class="text-gray-500">Please select project(s) to continue.</p>
                    } @else {
                    <p class="text-gray-500">Documents will be linked to selected project(s).</p>
                    }
                    }
                    }
                </div>
                }
            </div>

            <!-- Right Panel: Selected Files (only for "With KB" mode) -->
            @if(uploadMode === 'withKB') {
            <div class="flex flex-col flex-1 shadow-sm bg-white min-w-0 overflow-hidden ">
                <h2 class="font-bold text-lg mb-4 flex-shrink-0">Selected files</h2>
                <div class="mb-2">
                    <div class="text-sm font-semibold text-gray-700">{{ getScopeLabel() }}</div>
                </div>
                <div class="flex-1 overflow-y-auto">
                    @if(selectedKnowledgeBases.length === 0) {
                    <div class="flex flex-col items-center justify-center h-full text-center p-4">
                        <p class="text-gray-500">Select knowledge bases to add them</p>
                    </div>
                    } @else {
                    @if(scope === 'workspace') {
                    <!-- Workspace: Flat list -->
                    <div class="flex flex-col gap-2">
                        @for(kb of selectedKnowledgeBases; track kb._id) {
                        <div class="border rounded-base p-3 flex-shrink-0">
                            <div class="flex items-center gap-3 min-w-0">
                                <i [innerHTML]="getIcon() | sanitizeHtml" class="flex-shrink-0 w-6 h-6"></i>
                                <div class="min-w-0 flex-1">
                                    <div class="text-sm font-medium text-gray-900 truncate" [pTooltip]="kb.name"
                                        tooltipPosition="top">
                                        {{ kb.name || 'Kb name' }}
                                    </div>
                                    <div class="text-xs text-gray-500 truncate">
                                        {{ kb.description || 'Kb description' }}
                                    </div>
                                </div>
                                <i class="pi pi-trash text-danger cursor-pointer flex-shrink-0"
                                    (click)="removeSelection(kb)"></i>
                            </div>
                        </div>
                        }
                    </div>
                    } @else {
                    <!-- Project: Grouped by project -->
                    @if(shouldShowProjectGrouping()) {
                    <div class="flex flex-col gap-2">
                        @for(group of getGroupedKnowledgeBases(); track group.projectId) {
                        <div class="mb-4">
                            <div class="text-sm font-semibold text-gray-700 mb-2">
                                {{ group.projectName }} has {{ group.kbs.length }} KB{{ group.kbs.length > 1 ? 's' : ''
                                }}
                            </div>
                            <div class="flex flex-col gap-2">
                                @for(kb of group.kbs; track kb._id) {
                                <div class="border rounded-base p-3 flex-shrink-0">
                                    <div class="flex items-center gap-3 min-w-0">
                                        <i [innerHTML]="getIcon() | sanitizeHtml" class="flex-shrink-0 w-6 h-6"></i>
                                        <div class="min-w-0 flex-1">
                                            <div class="text-sm font-medium text-gray-900 truncate" [pTooltip]="kb.name"
                                                tooltipPosition="top">
                                                {{ kb.name || 'Kb name' }}
                                            </div>
                                            <div class="text-xs text-gray-500 truncate">
                                                {{ kb.description || 'Kb description' }}
                                            </div>
                                        </div>
                                        <i class="pi pi-trash text-danger cursor-pointer flex-shrink-0"
                                            (click)="removeSelection(kb)"></i>
                                    </div>
                                </div>
                                }
                            </div>
                        </div>
                        }
                    </div>
                    } @else {
                    <!-- Single project: Flat list -->
                    <div class="flex flex-col gap-2">
                        @for(kb of getSingleProjectKBs(); track kb._id) {
                        <div class="border rounded-base p-3 flex-shrink-0">
                            <div class="flex items-center gap-3 min-w-0">
                                <i [innerHTML]="getIcon() | sanitizeHtml" class="flex-shrink-0 w-6 h-6"></i>
                                <div class="min-w-0 flex-1">
                                    <div class="text-sm font-medium text-gray-900 truncate" [pTooltip]="kb.name"
                                        tooltipPosition="top">
                                        {{ kb.name || 'Kb name' }}
                                    </div>
                                    <div class="text-xs text-gray-500 truncate">
                                        {{ kb.description || 'Kb description' }}
                                    </div>
                                </div>
                                <i class="pi pi-trash text-danger cursor-pointer flex-shrink-0"
                                    (click)="removeSelection(kb)"></i>
                            </div>
                        </div>
                        }
                    </div>
                    }
                    }
                    }
                </div>
            </div>
            }
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button variant="filled" severity="primary" label="Upload" (clicked)="onUpload()" [loading]="uploading"
                [enableThrottle]="true" [disabled]="!isUploadEnabled()">
            </app-button>
        </div>
    </ng-template>
</app-dialog>

<!-- Create Knowledge Base Dialog -->
<app-dialog [(visible)]="createKbDialogVisible" (closed)="closeCreateKbDialog()" class="w-[90vw] max-w-[30rem]">
    <ng-template #headerTemplate>
        <div class="flex items-center justify-between w-full">
            <h3 class="font-bold text-base sm:text-lg">Create Knowledge Base</h3>
        </div>
    </ng-template>
    <ng-template #bodyTemplate>
        <form [formGroup]="createKbForm" class="flex flex-col gap-4 pt-1">
            <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-gray-700">
                    Name <span class="text-danger">*</span>
                </label>
                <input formControlName="name" placeholder="Enter knowledge base name"
                    class="w-full border outline-none rounded-base input p-2 text-sm" autocomplete="off" />
                @if(createKbControls['name'].invalid && (createKbControls['name'].dirty ||
                createKbControls['name'].touched)) {
                <span class="text-danger text-xs">Name is required.</span>
                }
            </div>
            <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-gray-700">
                    Description <span class="text-danger">*</span>
                </label>
                <textarea formControlName="description" placeholder="Enter knowledge base description"
                    class="w-full border outline-none rounded-base input p-2 text-sm resize-none" rows="3"
                    autocomplete="off"></textarea>
                @if(createKbControls['description'].invalid && (createKbControls['description'].dirty ||
                createKbControls['description'].touched)) {
                <span class="text-danger text-xs">Description is required.</span>
                }
            </div>
        </form>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button variant="outlined" severity="contrast" label="Cancel" icon="pi pi-times"
                (clicked)="closeCreateKbDialog()" />
            <app-button variant="filled" severity="primary" label="Create" icon="pi pi-plus" [loading]="createKbLoading"
                [disabled]="createKbForm.invalid" (clicked)="createKnowledgeBase()" [enableThrottle]="true" />
        </div>
    </ng-template>
</app-dialog>